import React, { useEffect, useState } from 'react';
import { Plus } from 'react-feather';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { TriangleRight } from '../../../assets/svgs/TriangleRight';
import SchoolSelector from '../SchoolSelector';
import { requestSchoolExams } from '../../../services/schools';
import { isVerifiedSchoolExam } from '../../../utils/nbmeExamUtils';
import { hapticsImpactLight } from '../../../utils/haptics';
import CustomModal from '../CustomModal';

interface ExamSelectProps {
  onClick: (examId: string) => void;
  addExam: () => void;
  updatePrimaryExam: (examId: string, setPrimary?: boolean) => Promise<void>;
}

const ExamSelect: React.FC<ExamSelectProps> = ({
  onClick,
  addExam,
  updatePrimaryExam,
}) => {
  const stepExams = useSelector(examsSelectors.selectStepExams);
  const shelfExams = useSelector(examsSelectors.selectShelfExams);
  const schoolExams = useSelector(examsSelectors.selectSchoolExams);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const school = useSelector((state: RootState) => state.school.school);

  const [showRequestConfirmation, setShowRequestConfirmation] = useState(false);

  useEffect(() => {
    if (school?.id && primaryExam) {
      if (
        isVerifiedSchoolExam(primaryExam) &&
        primaryExam.school?.id !== school.id
      ) {
        updatePrimaryExam(primaryExam.id, false);
      }
    }
  }, [school?.id]);

  const handleRequestExams = (schoolId: string) => {
    requestSchoolExams(schoolId);
    setShowRequestConfirmation(true);
  };

  return (
    <>
      <div className="available-exams">
        <div className="available-exams_top">
          <div className="available-exams_top_set available-exams_top_set--step">
            <div className="available-exams_top_set_title">Step</div>
            <div className="available-exams_top_set_exams">
              {stepExams.map((exam) => (
                <button
                  key={'exam-' + exam.id}
                  className={`available-exams_top_set_exams_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
                  onClick={() => onClick(exam.id)}
                >
                  <TriangleRight />
                  {exam.name}
                </button>
              ))}
            </div>
          </div>
          <div className="available-exams_top_set available-exams_top_set--shelf">
            <div className="available-exams_top_set_title">Shelf</div>
            <div className="available-exams_top_set_exams">
              {shelfExams.map((exam) => (
                <button
                  key={'exam-' + exam.id}
                  className={`available-exams_top_set_exams_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
                  onClick={() => onClick(exam.id)}
                >
                  <TriangleRight />
                  {exam.name.replace('Shelf Exam', '')}
                </button>
              ))}
            </div>
          </div>
          <div className="available-exams_top_set available-exams_top_set--school">
            <div className="available-exams_top_set_title">School</div>
            <SchoolSelector />
            {school && (
              <div
                className="available-exams_top_set_exams"
                style={
                  schoolExams.length > 0
                    ? { transform: 'translateY(-2px)' }
                    : {}
                }
              >
                {schoolExams.length > 0 ? (
                  schoolExams.map((exam) => (
                    <button
                      key={'exam-' + exam.id}
                      className={`available-exams_top_set_exams_exam ${exam.id === primaryExam?.id ? 'selected' : ''}`}
                      onClick={() => onClick(exam.id)}
                    >
                      <TriangleRight />
                      <span className="custom-exam-name">
                        {exam.name.includes(' | ')
                          ? exam.name.split(' | ')[1]
                          : exam.name}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="available-exams_top_set_exams_empty">
                    <div>No official exams yet</div>
                    <button
                      className="button button--request-exams"
                      onClick={() => {
                        handleRequestExams(school?.id);
                        hapticsImpactLight();
                      }}
                    >
                      Request Exams
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="available-exams_bottom">
          <button className="button button--add-exam" onClick={addExam}>
            <Plus />
            Add Exam
          </button>
        </div>
      </div>
      <CustomModal
        open={showRequestConfirmation}
        onClose={() => setShowRequestConfirmation(false)}
        name={'request-exams'}
        glass={true}
        blurs={true}
      >
        <div className="modal_content">
          <h3>Request Submitted</h3>
          <p>
            Thanks for your request! We will notify you when we have official
            exams for your school.
          </p>
          <p>
            <strong>In the meantime, what would you like to do?</strong>
          </p>
          <div className="actions">
            <button
              className="button button--glass"
              onClick={() => {
                setShowRequestConfirmation(false);
                hapticsImpactLight();
              }}
            >
              Select a Different Exam
            </button>
            <button
              className="button button--glass"
              onClick={() => {
                setShowRequestConfirmation(false);
                addExam();
                hapticsImpactLight();
              }}
            >
              Build My Own Exam
            </button>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default ExamSelect;
